// @ts-nocheck
import { Injectable } from '@angular/core';
import * as MapModule from 'ol/Map.js';
const Map = MapModule.default as typeof MapModule.default;
import Layer from 'ol/layer/Layer.js';
import { Dataset } from 'src/app/datasets/dataset';
import { Filter, FilterConfig } from 'src/app/datasets/filter';
import * as TileLayerModule from 'ol/layer/Tile.js';
const TileLayer = TileLayerModule.default as typeof TileLayerModule.default;
import * as XYZModule from 'ol/source/XYZ.js';
const XYZ = XYZModule.default as typeof XYZModule.default;
import * as ViewModule from 'ol/View.js';
const View = ViewModule.default as typeof ViewModule.default;
import { transformExtent } from 'ol/proj.js';
import { ATTRIBUTION, DEFAULT_EXTENT } from 'src/assets/constants/constants';
import { defaults as defaultInteractions } from 'ol/interaction.js';
import { defaults as defaultControls } from 'ol/control.js';
import proj4 from 'proj4';
import { register } from 'ol/proj/proj4.js';
import { DatasetEntry, Datasets } from 'src/app/components/map/map.component';
import { setBuffer } from 'src/app/utils/helpers';
import * as Ol3ParserModule from 'jsts/org/locationtech/jts/io/OL3Parser.js';
const Ol3Parser = Ol3ParserModule.default as typeof Ol3ParserModule.default;
import { LinearRing, LineString, MultiLineString, MultiPoint, MultiPolygon, Point, Polygon } from 'ol/geom.js';
import { Area } from '../area/area';
import { MapConfig } from 'src/app/datasets/map-config';
import { BehaviorSubject, ReplaySubject, Subject } from 'rxjs';
import type MapBrowserEvent from 'ol/MapBrowserEvent.js';
import { PointDetails } from 'src/app/models/pointDetails';

@Injectable({
    providedIn: 'root',
})
export class OLMapService {
    mapConfig: MapConfig;
    datasetEntry: DatasetEntry;

    details: Array<{
        label: string;
        value?: string;
        ratio?: number;
        level?: string;
    }>;
    parser: Ol3Parser = new Ol3Parser();

    layers: Array<{ name: string; layer: Layer; workspace?: string }> = [];

    detailsLoading: boolean = false;
    landlinePointDetails: PointDetails[] = [];
    detailPanelOpen: boolean = false;
    map: Map;
    readonly mapReady$ = new ReplaySubject<Map>(1);
    readonly mapClick$ = new Subject<any>();
    readonly detailChanges$ = new BehaviorSubject<{ revision: number }>({ revision: 0 });
    private detailRevision = 0;
    private lastMapClick?: { time: number; x: number; y: number; clientX?: number; clientY?: number };
    debugReceivedClickCount: number = 0;
    debugForwardedClickCount: number = 0;
    debugLastSource: string = 'none';
    debugLastCoordinate: string = '-';
    debugLastAcceptedSource: string = 'none';

    datasets: Datasets = [];
    dataset: Dataset;
    filter: Filter;
    filters: FilterConfig[];

    constructor() {
        this.parser.inject(Point, LineString, LinearRing, Polygon, MultiPoint, MultiLineString, MultiPolygon);
    }

    createMap() {
        const interactions = defaultInteractions({
            altShiftDragRotate: false,
            pinchRotate: false,
        });
        const controls = defaultControls({ rotate: false });
        this.map = new Map({
            interactions: interactions,
            controls: controls,
            target: 'map',
            layers: [
                new TileLayer({
                    maxZoom: 18,
                    source: new XYZ({
                        url: 'https://{a-c}.tile.openstreetmap.org/{z}/{x}/{y}.png',
                        attributions: ATTRIBUTION,
                    }),
                }),
            ],
            view: new View({
                center: [0, 0],
                minZoom: 6,
                maxZoom: 18,
            }),
        });
        // Retain the Belgium-centred default extent while leaving a little
        // more context around it, matching the compact atlas viewport.
        this.map.getView().fit(transformExtent(DEFAULT_EXTENT, 'EPSG:4326', 'EPSG:3857'), {
            padding: [24, 24, 24, 24],
        });
        this.map.getView();
        this.mapReady$.next(this.map);
    }
    handleMapClick(event: MapBrowserEvent<MouseEvent> | { map: Map; coordinate: [number, number] | number[]; originalEvent?: MouseEvent; source?: string }): void {
        if (!event?.map || !event?.coordinate) {
            return;
        }

        if (this.isMapControlInteraction(event.originalEvent)) {
            return;
        }

        const source = event.source ?? 'unknown';
        this.recordDebugClick(event.coordinate, source);

        if (this.isDuplicateMapClick(event)) {
            return;
        }

        this.debugForwardedClickCount += 1;
        this.debugLastAcceptedSource = source;
        this.detailPanelOpen = true;
        this.mapClick$.next(event);
    }

    private isMapControlInteraction(event?: MouseEvent): boolean {
        const target = event?.target;
        if (!(target instanceof Element)) {
            return false;
        }

        return !!target.closest(
            '.ol-control, .map-side-panel, button, input, select, textarea, mat-select, [role="dialog"]'
        );
    }


    private recordDebugClick(coordinate: [number, number] | number[], source: string): void {
        const [x, y] = coordinate ?? [];
        this.debugReceivedClickCount += 1;
        this.debugLastSource = source;
        this.debugLastCoordinate = Number.isFinite(x) && Number.isFinite(y)
            ? `${x.toFixed(2)}, ${y.toFixed(2)}`
            : 'invalid';
    }

    private isDuplicateMapClick(event: { coordinate: [number, number] | number[]; originalEvent?: MouseEvent }): boolean {
        const [x, y] = event.coordinate ?? [];
        if (!Number.isFinite(x) || !Number.isFinite(y)) {
            return true;
        }

        const time = event.originalEvent?.timeStamp ?? Date.now();
        const clientX = event.originalEvent?.clientX;
        const clientY = event.originalEvent?.clientY;
        const last = this.lastMapClick;
        this.lastMapClick = { time, x, y, clientX, clientY };

        if (!last || time - last.time >= 500) {
            return false;
        }

        const sameScreenPoint = Number.isFinite(clientX) && Number.isFinite(clientY)
            && Number.isFinite(last.clientX) && Number.isFinite(last.clientY)
            && Math.abs((last.clientX as number) - (clientX as number)) < 2
            && Math.abs((last.clientY as number) - (clientY as number)) < 2;

        return sameScreenPoint || (Math.abs(last.x - x) < 1 && Math.abs(last.y - y) < 1);
    }
    notifyDetailStateChanged(): void {
        this.detailRevision += 1;
        this.detailChanges$.next({ revision: this.detailRevision });
    }
    closeDetailPanel(): void {
        this.detailPanelOpen = false;
        this.details = [];
        this.landlinePointDetails = [];
        this.detailsLoading = false;
    }
    reset(): void {
        // The atlas route reuses this map host between child pages. Keep the
        // OpenLayers instance, but remove all layers and state owned by the
        // page that is being left. Keeping the previous route's filters here
        // makes the next atlas page initialise against an incompatible state
        // after its layers have already been removed.
        this.layers.forEach((entry) => this.map?.removeLayer(entry.layer));
        this.map?.getOverlays().clear();

        this.layers = [];
        this.mapConfig = {} as MapConfig;
        this.datasetEntry = undefined;
        this.dataset = undefined;
        this.filter = undefined;
        this.filters = [];
        this.details = [];
        this.landlinePointDetails = [];
        this.detailsLoading = false;
        this.detailPanelOpen = false;
    }
    setProj4Defs() {
        proj4.defs(
            'EPSG:31370',
            '+proj=lcc +lat_1=51.16666723333333 +lat_2=49.8333339 +lat_0=90 +lon_0=4.367486666666666 +x_0=150000.013 +y_0=5400088.438 +ellps=intl +towgs84=-106.869,52.2978,-103.724,0.3366,-0.457,1.8422,-1.2747 +units=m +no_defs'
        );
        register(proj4);
    }
    searchArea(area: Area) {
        if (!(area && (area.center || area.contour))) return;

        if (area.center) {
            const geom = setBuffer(area.center.coordinates[0], this.parser);
            this.map.getView().fit(geom.getExtent());
        }
        if (area.contour) {
            const geom = new MultiPolygon(area.contour.coordinates[0]).transform('EPSG:31370', 'EPSG:3857');
            this.map.getView().fit(geom.getExtent());
        }
    }

    readWfsCapabilities(val: string): Array<{ featureType: string; name: string }> {
        const capabilities = new DOMParser().parseFromString(val, 'text/xml');
        console.log('capabilities', capabilities);

        const featureTypeList = capabilities.querySelector('FeatureTypeList');
        console.log('featureTypeList', featureTypeList);

        const featureTypes = featureTypeList.getElementsByTagName('FeatureType');

        let featureDescriptor = [];
        for (let i = 0; i < featureTypes.length; i++) {
            let featureType = featureTypes[i];
            let name = featureType.getElementsByTagName('Name')[0]?.textContent;
            featureDescriptor.push({ featureType: featureType, name: name });
        }

        return featureDescriptor;
    }
}




